import { AlertTriangle, Check } from "lucide-react";
import type { FC } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

import { CaseDot } from "../components/CasePicker";
import type { TimeEntry } from "../types";
import { formatHours } from "../utils";
import { activityLine, isOpen } from "./homeModel";

type Props = {
  entry: TimeEntry;
  /** Show a checkbox (the evening card); the carry-over card lists only. */
  selectable: boolean;
  checked: boolean;
  onToggle?: () => void;
  first?: boolean;
};

/** One entry on a Home time card: state, case, narrative and activity, hours. */
export const ReviewRow: FC<Props> = ({
  entry,
  selectable,
  checked,
  onToggle,
  first
}) => {
  const open = isOpen(entry);
  const needsCase = !entry.individual_id;
  const detail = activityLine(entry);
  return (
    <div
      data-testid="review-row"
      data-entry={entry.id}
      className={cn(
        "grid grid-cols-[16px_170px_minmax(0,1fr)_48px] items-center gap-3 px-3.5 py-2.5",
        !first && "border-t border-line-2",
        !open && "opacity-60"
      )}
    >
      {!open ? (
        <Check className="size-3.5 text-success" aria-label="Released" />
      ) : needsCase ? (
        <AlertTriangle
          className="size-3.5 text-warn"
          aria-label="Needs a case"
        />
      ) : selectable ? (
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          aria-label={`Release ${entry.case_label}`}
          className="size-3.5"
        />
      ) : (
        <span />
      )}
      {needsCase ? (
        <span className="truncate text-base font-medium text-warn">
          Needs a case
        </span>
      ) : (
        <span className="flex min-w-0 items-center gap-1.5 text-base font-medium">
          <CaseDot id={entry.individual_id} />
          <span className="truncate">
            {entry.client_label || entry.case_label}
          </span>
        </span>
      )}
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-base">
          {entry.narrative || "No narrative yet"}
        </span>
        {detail ? (
          <span className="truncate text-sm text-fg-3">{detail}</span>
        ) : null}
      </span>
      <span className="text-right text-base font-medium tabular-nums">
        {formatHours(entry.duration_hours)}
      </span>
    </div>
  );
};
